import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LeadsLayout } from '@/components/LeadsLayout';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Auth header helper
const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

const PRIORITY_STYLES = {
  hot: { bg: 'bg-red-100', text: 'text-red-700', label: 'Hot' },
  warm: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Warm' },
  cold: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Cold' }
};

const STAGE_STYLES = {
  new: { color: 'bg-blue-500', label: 'New Lead' },
  contacted: { color: 'bg-cyan-500', label: 'Contacted' },
  interested: { color: 'bg-green-500', label: 'Interested' },
  test_drive: { color: 'bg-purple-500', label: 'Test Drive' },
  negotiation: { color: 'bg-orange-500', label: 'Negotiation' },
  finance_pending: { color: 'bg-pink-500', label: 'Finance' },
  dp_pending: { color: 'bg-indigo-500', label: 'DP Pending' },
  won: { color: 'bg-emerald-500', label: 'Won' },
  lost: { color: 'bg-red-500', label: 'Lost' }
};

// Date format helper
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

// Generate avatar color from name
const getAvatarColor = (name) => {
  const colors = [
    'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-purple-500', 
    'bg-pink-500', 'bg-indigo-500', 'bg-teal-500', 'bg-orange-500'
  ];
  const index = name ? name.charCodeAt(0) % colors.length : 0;
  return colors[index];
};

// Check if follow-up time has lapsed
const isFollowUpLapsed = (dateStr, timeStr) => {
  if (!dateStr) return false;
  const now = new Date();
  const followUpDate = new Date(dateStr);
  
  if (timeStr) {
    const [hours, minutes] = timeStr.split(':');
    followUpDate.setHours(parseInt(hours) || 0, parseInt(minutes) || 0, 0, 0);
  } else {
    followUpDate.setHours(17, 0, 0, 0);
  }
  
  return now > followUpDate;
};

function FollowUpDueReport({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState({ due_today: [], overdue: [], total_due: 0, total_overdue: 0 });
  const [activeTab, setActiveTab] = React.useState('overdue');
  const [reminderLead, setReminderLead] = React.useState(null);
  const [dismissedLeadIds, setDismissedLeadIds] = React.useState([]);
  const [reminderDismissedCompletely, setReminderDismissedCompletely] = React.useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = React.useState(false);
  const [selectedLead, setSelectedLead] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [rowsPerPage] = React.useState(10);

  React.useEffect(() => {
    fetchData();
  }, []);

  // Reset page when tab changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // Check for lapsed follow-ups
  React.useEffect(() => {
    if (reminderDismissedCompletely) return; // User dismissed all reminders
    
    if (data.due_today.length > 0) {
      const lapsedLeads = data.due_today.filter(lead => 
        isFollowUpLapsed(lead.next_follow_up_date, lead.next_follow_up_time) &&
        !dismissedLeadIds.includes(lead.id)
      );
      if (lapsedLeads.length > 0 && !reminderLead) {
        setReminderLead(lapsedLeads[0]);
      }
    }
  }, [data.due_today, reminderLead, dismissedLeadIds, reminderDismissedCompletely]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/leads/follow-up/due`, getAuthHeader());
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load follow-up data');
    }
    setLoading(false);
  };

  const currentList = activeTab === 'overdue' ? data.overdue : data.due_today;
  const totalPages = Math.ceil(currentList.length / rowsPerPage);
  const paginatedList = currentList.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const handleCall = (lead, e) => {
    if (e) e.stopPropagation();
    window.open(`tel:${lead.mobile}`, '_self');
  };

  const handleWhatsApp = (lead, e) => {
    if (e) e.stopPropagation();
    const message = `Hi ${lead.first_name}, this is a follow-up regarding your interest in ${lead.interested_model || 'our vehicles'}. How can I assist you today?`;
    { const _a = document.createElement('a'); _a.href = `https://web.whatsapp.com/send?phone=91${lead.mobile}&text=${encodeURIComponent(message)}`; _a.target = '_blank'; _a.rel = 'noopener noreferrer'; document.body.appendChild(_a); _a.click(); document.body.removeChild(_a); }
  };

  const openFollowUpModal = (lead, e) => {
    if (e) e.stopPropagation();
    setSelectedLead(lead);
    setShowFollowUpModal(true);
  };

  const handleExportExcel = () => {
    const exportData = currentList.map(lead => ({
      'Lead #': lead.lead_no,
      'Customer Name': `${lead.first_name} ${lead.last_name || ''}`,
      'Mobile': lead.mobile,
      'Email': lead.email || '-',
      'Interested Model': lead.interested_model || '-',
      'Stage': STAGE_STYLES[lead.stage]?.label || lead.stage,
      'Priority': lead.priority || '-',
      'Sales Rep': lead.assigned_to || '-',
      'Follow-up Date': formatDate(lead.next_follow_up_date),
      'Follow-up Time': lead.next_follow_up_time || '-'
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, activeTab === 'overdue' ? 'Overdue' : 'Due Today');
    XLSX.writeFile(wb, `follow-up-${activeTab}-${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel exported successfully!');
  };

  // Pagination component
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    for (let i = 1; i <= Math.min(5, totalPages); i++) {
      let pageNum;
      if (totalPages <= 5) {
        pageNum = i;
      } else if (currentPage <= 3) {
        pageNum = i;
      } else if (currentPage >= totalPages - 2) {
        pageNum = totalPages - 5 + i;
      } else {
        pageNum = currentPage - 2 + i - 1;
      }
      pageNumbers.push(pageNum);
    }

    return (
      <div className="flex items-center justify-center gap-2 mt-6">
        <button
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
          className="p-2 rounded-lg border border-gray-200 text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>
        <button
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className="p-2 rounded-lg border border-gray-200 text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <span className="text-sm text-gray-600 px-2">
          Page {currentPage} of {totalPages}
        </span>

        {pageNumbers.map(pageNum => (
          <button
            key={pageNum}
            onClick={() => setCurrentPage(pageNum)}
            className={`w-8 h-8 rounded-lg text-sm font-medium ${
              currentPage === pageNum
                ? 'bg-green-600 text-white'
                : 'border border-gray-200 text-gray-600 hover:bg-gray-100'
            }`}
          >
            {pageNum}
          </button>
        ))}

        <button
          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          className="p-2 rounded-lg border border-gray-200 text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <button
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
          className="p-2 rounded-lg border border-gray-200 text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    );
  };

  return (
    <LeadsLayout user={user} onLogout={onLogout}>
      <div className="space-y-6 p-4 md:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Follow-up Due</h1>
            <p className="text-gray-500 text-sm mt-1">
              {currentList.length} leads found
              {currentList.length > 0 && ` • Showing ${Math.min((currentPage - 1) * rowsPerPage + 1, currentList.length)}-${Math.min(currentPage * rowsPerPage, currentList.length)} of ${currentList.length}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportExcel}
              className="px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export
            </button>
            <button
              onClick={() => navigate('/leads')}
              className="px-3 py-2 text-gray-600 border border-gray-300 text-sm rounded-lg hover:bg-gray-100"
            >
              ← Back
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-4">
              <div 
                onClick={() => setActiveTab('overdue')}
                className={`rounded-xl p-4 cursor-pointer transition-all ${
                  activeTab === 'overdue' 
                    ? 'bg-red-500 text-white shadow-lg ring-2 ring-red-300' 
                    : 'bg-red-50 text-red-700 hover:bg-red-100'
                }`}
              >
                <p className={`text-3xl font-bold ${activeTab === 'overdue' ? 'text-white' : 'text-red-700'}`}>
                  {data.total_overdue}
                </p>
                <p className={`text-sm ${activeTab === 'overdue' ? 'text-red-100' : 'text-red-600'}`}>
                  Overdue
                </p>
              </div>
              
              <div 
                onClick={() => setActiveTab('today')}
                className={`rounded-xl p-4 cursor-pointer transition-all ${
                  activeTab === 'today' 
                    ? 'bg-blue-500 text-white shadow-lg ring-2 ring-blue-300' 
                    : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                }`}
              >
                <p className={`text-3xl font-bold ${activeTab === 'today' ? 'text-white' : 'text-blue-700'}`}>
                  {data.total_due}
                </p>
                <p className={`text-sm ${activeTab === 'today' ? 'text-blue-100' : 'text-blue-600'}`}>
                  Due Today
                </p>
              </div>
            </div>

            {/* Leads List - 2-Row Card Layout */}
            {currentList.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl shadow-sm">
                <div className="text-5xl mb-4">✅</div>
                <p className="text-gray-500 text-lg">
                  {activeTab === 'overdue' ? 'No overdue follow-ups!' : 'No follow-ups due today!'}
                </p>
                <p className="text-gray-400">Great job keeping up with your leads!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {paginatedList.map(lead => {
                  const priorityStyle = PRIORITY_STYLES[lead.priority] || PRIORITY_STYLES.warm;
                  const stageStyle = STAGE_STYLES[lead.stage] || STAGE_STYLES.new;
                  const avatarColor = getAvatarColor(lead.first_name);
                  const lapsed = activeTab === 'today' && isFollowUpLapsed(lead.next_follow_up_date, lead.next_follow_up_time);
                  
                  return (
                    <div 
                      key={lead.id}
                      className={`bg-white rounded-xl shadow-sm border overflow-hidden ${
                        lapsed ? 'border-red-300 ring-2 ring-red-200' : 'border-gray-100'
                      }`}
                      data-testid={`followup-lead-card-${lead.id}`}
                    >
                      {/* Row 1: Lead Info */}
                      <div className="p-3 border-b border-gray-100">
                        <div className="flex items-start gap-3">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${avatarColor} flex-shrink-0 ${lapsed ? 'ring-2 ring-red-500' : ''}`}>
                            {lead.first_name?.[0]?.toUpperCase() || '?'}
                            {lead.last_name?.[0]?.toUpperCase() || ''}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className={`font-semibold truncate ${lapsed ? 'text-red-800' : 'text-gray-800'}`}>
                                {lead.first_name} {lead.last_name || ''}
                              </h3>
                              <span className="text-xs text-gray-500">#{lead.lead_no}</span>
                              {lapsed && (
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-600 text-white animate-pulse">
                                  LAPSED
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <a href={`tel:${lead.mobile}`} className="text-sm text-blue-600 hover:underline">
                                {lead.mobile}
                              </a>
                              {lead.email && (
                                <span className="text-xs text-gray-500 truncate max-w-[120px]">
                                  {lead.email}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium text-white ${stageStyle.color}`}>
                                {stageStyle.label}
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityStyle.bg} ${priorityStyle.text}`}>
                                {priorityStyle.label}
                              </span>
                              {lead.lead_grade && (
                                <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                  lead.lead_grade === 'A' ? 'bg-green-100 text-green-700' :
                                  lead.lead_grade === 'B' ? 'bg-blue-100 text-blue-700' :
                                  'bg-yellow-100 text-yellow-700'
                                }`}>
                                  Grade {lead.lead_grade}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Last Activity Section - matching All Leads format */}
                      {lead.follow_up_history && lead.follow_up_history.length > 0 && (
                        <div className="px-3 py-2 border-b border-gray-100 bg-white">
                          <div className="text-xs space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-gray-500 font-medium">Last Activity:</span>
                              {/* Call Status */}
                              {lead.follow_up_history[0]?.call_status && (
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                  lead.follow_up_history[0].call_status === 'Connected' 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-red-100 text-red-700'
                                }`}>
                                  📞 {lead.follow_up_history[0].call_status}
                                </span>
                              )}
                              {/* Follow-up Status */}
                              {lead.follow_up_history[0]?.followup_status && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[10px] font-medium">
                                  {lead.follow_up_history[0].followup_status}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              {/* Sub Lead Status */}
                              {lead.follow_up_history[0]?.sub_lead_status && (
                                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-[10px] font-medium">
                                  {lead.follow_up_history[0].sub_lead_status}
                                </span>
                              )}
                              {/* Date & Time */}
                              <span className="text-gray-400 ml-auto text-[10px]">
                                {lead.follow_up_history[0]?.created_at ? new Date(lead.follow_up_history[0].created_at).toLocaleDateString('en-GB') : ''}
                                {lead.follow_up_history[0]?.created_at && (
                                  <span className="ml-1">
                                    {new Date(lead.follow_up_history[0].created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                )}
                              </span>
                            </div>
                            {/* Remarks */}
                            {lead.follow_up_history[0]?.remarks && (
                              <div className="text-gray-600 bg-gray-100 rounded px-2 py-1 mt-1">
                                <span className="text-gray-400 mr-1">📝</span>
                                {lead.follow_up_history[0].remarks}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      {/* Fallback to activities if no follow_up_history */}
                      {(!lead.follow_up_history || lead.follow_up_history.length === 0) && lead.activities && lead.activities.length > 0 && (
                        <div className="px-3 py-2 border-b border-gray-100 bg-white">
                          <div className="text-xs">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-gray-500 font-medium">Last Activity:</span>
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-[10px] font-medium">
                                {lead.activities[0]?.activity_type?.replace(/_/g, ' ') || 'Activity'}
                              </span>
                              {lead.activities[0]?.description && (
                                <span className="text-gray-600 truncate max-w-[200px]">
                                  {lead.activities[0].description}
                                </span>
                              )}
                              <span className="text-gray-400 ml-auto text-[10px]">
                                {lead.activities[0]?.timestamp ? new Date(lead.activities[0].timestamp).toLocaleDateString('en-GB') : ''}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Row 2: Details + Actions */}
                      <div className="p-3 bg-gray-50">
                        <div className="flex items-center justify-between gap-2">
                          {/* Left: Details */}
                          <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                            <div>
                              <span className="text-gray-500 block">Interest</span>
                              <span className="text-gray-800 font-medium truncate block">
                                {lead.interested_model || '-'}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500 block">Source</span>
                              <span className="text-gray-800 font-medium truncate block">{lead.lead_source || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500 block">Sales Rep</span>
                              <span className="text-gray-800 font-medium truncate block">{lead.assigned_to || '-'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500 block">Follow-up</span>
                              <span className={`font-medium ${lapsed || activeTab === 'overdue' ? 'text-red-600' : 'text-blue-600'}`}>
                                {formatDate(lead.next_follow_up_date)}
                                {lead.next_follow_up_time && (
                                  <span className="text-xs ml-1">{lead.next_follow_up_time}</span>
                                )}
                              </span>
                            </div>
                          </div>

                          {/* Right: Action Icons */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              onClick={(e) => openFollowUpModal(lead, e)}
                              className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                              title="View Follow-up History"
                              data-testid={`followup-view-btn-${lead.id}`}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => navigate(`/leads/edit/${lead.id}`)}
                              className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                              title="Edit"
                              data-testid={`followup-edit-btn-${lead.id}`}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <a
                              href={`tel:${lead.mobile}`}
                              className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-colors"
                              title="Call"
                              data-testid={`followup-call-btn-${lead.id}`}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                            </a>
                            <button
                              onClick={(e) => handleWhatsApp(lead, e)}
                              className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                              title="WhatsApp"
                              data-testid={`followup-whatsapp-btn-${lead.id}`}
                            >
                              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Pagination */}
                {renderPagination()}
              </div>
            )}
          </>
        )}

        {/* Follow-up Reminder Popup */}
        {reminderLead && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
              <div className="bg-red-500 text-white p-4">
                <div className="flex items-center gap-3">
                  <span className="text-3xl animate-bounce">⏰</span>
                  <div>
                    <h3 className="text-lg font-bold">Follow-up Reminder!</h3>
                    <p className="text-red-100 text-sm">Time has lapsed for this follow-up</p>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-lg ${getAvatarColor(reminderLead.first_name)}`}>
                    {reminderLead.first_name?.[0]?.toUpperCase() || '?'}
                    {reminderLead.last_name?.[0]?.toUpperCase() || ''}
                  </div>
                  <div>
                    <p className="font-bold text-gray-800 text-lg">
                      {reminderLead.first_name} {reminderLead.last_name || ''}
                    </p>
                    <p className="text-gray-500">{reminderLead.mobile}</p>
                    {reminderLead.interested_model && (
                      <p className="text-gray-500">{reminderLead.interested_model}</p>
                    )}
                  </div>
                </div>
                
                <div className="bg-red-50 rounded-lg p-3 mb-4">
                  <p className="text-red-700 text-sm">
                    <strong>Scheduled:</strong> {formatDate(reminderLead.next_follow_up_date)} 
                    {reminderLead.next_follow_up_time && ` at ${reminderLead.next_follow_up_time}`}
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      handleCall(reminderLead);
                      setReminderLead(null);
                    }}
                    className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    Call Now
                  </button>
                  <button
                    onClick={() => {
                      handleWhatsApp(reminderLead);
                      setReminderLead(null);
                    }}
                    className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                  >
                    WhatsApp
                  </button>
                </div>
                
                <button
                  onClick={() => {
                    // Add current lead to dismissed list
                    if (reminderLead) {
                      setDismissedLeadIds(prev => [...prev, reminderLead.id]);
                    }
                    
                    // Find remaining lapsed leads (excluding dismissed ones)
                    const remainingLapsedLeads = data.due_today.filter(lead => 
                      isFollowUpLapsed(lead.next_follow_up_date, lead.next_follow_up_time) &&
                      !dismissedLeadIds.includes(lead.id) &&
                      lead.id !== reminderLead?.id
                    );
                    
                    if (remainingLapsedLeads.length > 0) {
                      setReminderLead(remainingLapsedLeads[0]);
                    } else {
                      setReminderLead(null);
                      setReminderDismissedCompletely(true);
                    }
                  }}
                  className="w-full mt-3 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Follow-up Modal */}
        {showFollowUpModal && selectedLead && (
          <FollowUpModal 
            lead={selectedLead} 
            onClose={() => {
              setShowFollowUpModal(false);
              setSelectedLead(null);
            }}
            onSuccess={() => {
              setShowFollowUpModal(false);
              setSelectedLead(null);
              fetchData();
              toast.success('Follow-up saved!');
            }}
          />
        )}
      </div>
    </LeadsLayout>
  );
}

// Follow-up Modal Component
// Follow-up status options - same as LeadsList.js
const CALL_STATUS_OPTIONS = ['Connected', 'Not Connected'];
const FOLLOWUP_STATUS_OPTIONS = ['Spoke to Customer', 'Customer was Busy', 'No Response', 'Wrong Number', 'Callback Requested'];
const SUB_LEAD_STATUS_OPEN = ['EMI Related', 'Exchange Related', 'Test Drive Follow up', 'Visit Follow up', 'Home Test Drive', 'Price Enquiry', 'Finance Enquiry'];
const SUB_LEAD_STATUS_CLOSE = ['Delivery Done', 'Booking Done', 'Lost - Not Interested', 'Lost - Bought Elsewhere', 'Lost - Budget Issue'];

function FollowUpModal({ lead, onClose, onSuccess }) {
  const [activeTab, setActiveTab] = React.useState('add');
  const [followUpData, setFollowUpData] = React.useState({
    call_status: '',
    followup_status: '',
    sub_lead_status: '',
    sub_lead_category: 'open',
    next_follow_up_date: '',
    next_follow_up_time: '',
    remarks: ''
  });
  const [loading, setLoading] = React.useState(false);
  const [history, setHistory] = React.useState([]);
  const [leadDetails, setLeadDetails] = React.useState(lead);

  React.useEffect(() => {
    fetchLeadDetails();
    fetchFollowUpHistory();
  }, [lead.id]);

  const fetchLeadDetails = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/leads/${lead.id}`, getAuthHeader());
      setLeadDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch lead details');
    }
  };

  const fetchFollowUpHistory = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/leads/${lead.id}/follow-up-history`, getAuthHeader());
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to fetch history');
      setHistory(lead.activities || []);
    }
  };

  const handleSave = async () => {
    if (!followUpData.call_status) {
      toast.error('Please select Call Status');
      return;
    }
    
    setLoading(true);
    try {
      // Combine date and time
      let nextFollowUp = followUpData.next_follow_up_date;
      if (nextFollowUp && followUpData.next_follow_up_time) {
        nextFollowUp = `${followUpData.next_follow_up_date}T${followUpData.next_follow_up_time}`;
      }

      await axios.post(`${BACKEND_URL}/api/leads/${lead.id}/follow-up`, {
        call_status: followUpData.call_status,
        followup_status: followUpData.followup_status,
        sub_lead_status: followUpData.sub_lead_status,
        next_follow_up_date: nextFollowUp,
        remarks: followUpData.remarks
      }, getAuthHeader());
      
      toast.success('Follow-up saved successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save follow-up');
    }
    setLoading(false);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-800">
              Follow-up: {leadDetails.first_name} {leadDetails.last_name}
            </h3>
            <p className="text-sm text-gray-500">{leadDetails.mobile} • {leadDetails.interested_model}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>

        {/* Content - 2 Column Layout */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left Column - Customer Details & Add Follow-up */}
          <div className="w-1/2 border-r border-gray-200 overflow-y-auto p-4">
            {/* Tabs */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setActiveTab('add')}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  activeTab === 'add' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                Add Follow-up
              </button>
              <button
                onClick={() => setActiveTab('details')}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  activeTab === 'details' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                Customer Details
              </button>
            </div>

            {activeTab === 'add' ? (
              <div className="space-y-4">
                {/* Call Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Call Status *</label>
                  <div className="flex gap-2">
                    {CALL_STATUS_OPTIONS.map(status => (
                      <button
                        key={status}
                        onClick={() => setFollowUpData(prev => ({ ...prev, call_status: status }))}
                        className={`px-4 py-2 rounded-lg text-sm font-medium border ${
                          followUpData.call_status === status
                            ? status === 'Connected' ? 'bg-green-100 border-green-500 text-green-700' : 'bg-red-100 border-red-500 text-red-700'
                            : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Follow-up Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Follow-up Status</label>
                  <select
                    value={followUpData.followup_status}
                    onChange={(e) => setFollowUpData(prev => ({ ...prev, followup_status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">-- Select --</option>
                    {FOLLOWUP_STATUS_OPTIONS.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>

                {/* Sub Lead Status */}
                {followUpData.followup_status === 'Spoke to Customer' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sub Lead Status</label>
                    <div className="flex gap-2 mb-2">
                      <button
                        onClick={() => setFollowUpData(prev => ({ ...prev, sub_lead_category: 'open', sub_lead_status: '' }))}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          followUpData.sub_lead_category === 'open' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        Open
                      </button>
                      <button
                        onClick={() => setFollowUpData(prev => ({ ...prev, sub_lead_category: 'close', sub_lead_status: '' }))}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          followUpData.sub_lead_category === 'close' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        Close
                      </button>
                    </div>
                    <select
                      value={followUpData.sub_lead_status}
                      onChange={(e) => setFollowUpData(prev => ({ ...prev, sub_lead_status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">-- Select --</option>
                      {(followUpData.sub_lead_category === 'open' ? SUB_LEAD_STATUS_OPEN : SUB_LEAD_STATUS_CLOSE).map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Next Follow-up Date & Time */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Next Follow-up Date</label>
                    <input
                      type="date"
                      value={followUpData.next_follow_up_date}
                      onChange={(e) => setFollowUpData(prev => ({ ...prev, next_follow_up_date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                    <input
                      type="time"
                      value={followUpData.next_follow_up_time}
                      onChange={(e) => setFollowUpData(prev => ({ ...prev, next_follow_up_time: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>

                {/* Remarks */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                  <textarea
                    value={followUpData.remarks}
                    onChange={(e) => setFollowUpData(prev => ({ ...prev, remarks: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows={3}
                    placeholder="Add notes about this follow-up..."
                  />
                </div>

                {/* Save Button */}
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                >
                  {loading ? 'Saving...' : 'Save Follow-up'}
                </button>
              </div>
            ) : (
              /* Customer Details Tab */
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-gray-500">Name</span>
                    <p className="font-medium">{leadDetails.first_name} {leadDetails.last_name}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-gray-500">Mobile</span>
                    <p className="font-medium">{leadDetails.mobile}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-gray-500">Email</span>
                    <p className="font-medium">{leadDetails.email || '-'}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-gray-500">Lead Source</span>
                    <p className="font-medium">{leadDetails.lead_source}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-gray-500">Interest</span>
                    <p className="font-medium">{leadDetails.interested_brand} {leadDetails.interested_model}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-gray-500">Stage</span>
                    <p className="font-medium">{leadDetails.stage}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-gray-500">Priority</span>
                    <p className="font-medium">{leadDetails.priority}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <span className="text-gray-500">Assigned To</span>
                    <p className="font-medium">{leadDetails.assigned_to || '-'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Follow-up History */}
          <div className="w-1/2 overflow-y-auto p-4 bg-gray-50">
            <h4 className="font-semibold text-gray-800 mb-3">Follow-up History</h4>
            {history.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No follow-up history yet</p>
            ) : (
              <div className="space-y-3">
                {history.map((item, idx) => (
                  <div key={idx} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        item.call_status === 'Connected' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {item.call_status || item.activity_type || 'Activity'}
                      </span>
                      <span className="text-xs text-gray-500">{formatDateTime(item.timestamp || item.created_at)}</span>
                    </div>
                    {item.followup_status && (
                      <p className="text-sm text-gray-700"><strong>Status:</strong> {item.followup_status}</p>
                    )}
                    {item.sub_lead_status && (
                      <p className="text-sm text-gray-700"><strong>Sub Status:</strong> {item.sub_lead_status}</p>
                    )}
                    {item.remarks && (
                      <p className="text-sm text-gray-600 mt-1">{item.remarks}</p>
                    )}
                    {item.description && !item.remarks && (
                      <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FollowUpDueReport;
